import type {
	ListBuilderColumn,
	ListBuilderConfiguration,
	ListBuilderList,
} from '../../types';

/**
 * Compliance > Federal Exclusion (PD-13954) — customer accounts, or people
 * the state boards link to them (a pharmacist-in-charge, an owner), that
 * match a party on the OIG List of Excluded Individuals / Entities. The
 * Compliance dashboard's Federal Exclusion card opens it; it is reviewed and
 * marked off the way the Adverse Action card is (Chris, 2026-10-07).
 *
 * Rows come from `GET /licenses/exclusion-matches` (crm-api
 * FederalExclusionService), which joins the tenant's own screening results
 * (`client_exclusion_match`) to the OIG list in mission control. Like Lead
 * Finder, `objectTable: 'lm_exclusion_match'` only scopes the field and filter
 * configs: it is a stable key for this list's saved field and filter
 * settings, kept from the first build, and names no table.
 *
 * Tabs are lists of the same config, each locking the `exclusionView` filter:
 *   Strong Matches     — the same NPI, or the same name at the same address.
 *                        The card's figure and its default list.
 *   Check Your Records — the same name in the same state, and nothing else to
 *                        compare: a name alone can belong to someone else.
 *   Reviewed           — the matches a decision is hiding, with the decision.
 * Only current customers (a sale in 90 days), like every other card.
 */

export const FEDERAL_EXCLUSION_LIST_KEY = 'license_exclusion';

/** Where the list lives. The card and the menu link here. */
export const FEDERAL_EXCLUSION_PATH = '/compliance/exclusions';

/**
 * The tab ids. Part of the URL contract — a bookmarked or shared link carries
 * one — so a change breaks saved links; the config test pins them.
 */
export const FEDERAL_EXCLUSION_LIST_IDS = {
	strong: 'compliance-exclusion-strong',
	nameOnly: 'compliance-exclusion-name-only',
	reviewed: 'compliance-exclusion-reviewed',
} as const;

export type FederalExclusionListId =
	(typeof FEDERAL_EXCLUSION_LIST_IDS)[keyof typeof FEDERAL_EXCLUSION_LIST_IDS];

/** A tab's URL. */
export function federalExclusionListPath(listId: FederalExclusionListId): string {
	return `${FEDERAL_EXCLUSION_PATH}?list=${listId}`;
}

const OBJ = 'lm_exclusion_match';
const plain = { size: 'sm' as const, colorRule: { type: 'default' as const } };
const muted = {
	size: 'xs' as const,
	colorRule: { type: 'theme' as const, themeColor: 'textSecondary' as const },
};
const lineBreak = (id: string): ListBuilderColumn['fields'][number] => ({
	id,
	dataConfig: { type: 'custom_text', customText: '' },
	displayStyle: 'line_break',
});
/** A field shown only when the row has a value for it (OIG publishes many as blank). */
const optionalField = (
	id: string,
	fieldName: string,
	style: ListBuilderColumn['fields'][number]['styleConfig'],
	displayStyle: ListBuilderColumn['fields'][number]['displayStyle'] = 'text',
): ListBuilderColumn['fields'][number] => ({
	...field(id, fieldName, style, displayStyle),
	conditionalVisibility: { field: fieldName, condition: 'is_not_null' },
});
const field = (
	id: string,
	fieldName: string,
	style: ListBuilderColumn['fields'][number]['styleConfig'],
	displayStyle: ListBuilderColumn['fields'][number]['displayStyle'] = 'text',
): ListBuilderColumn['fields'][number] => ({
	id,
	dataConfig: { type: 'field', field: { fieldName, objectTable: OBJ } },
	styleConfig: style,
	displayStyle,
});

/** Account (links to the client) over its city, state and ZIP. */
const accountColumn: ListBuilderColumn = {
	id: 'exclusion-account',
	fields: [
		{
			id: 'exclusion-account-name',
			link: { type: 'custom', enabled: true, urlTemplate: '/clients/{clientId}' },
			dataConfig: {
				type: 'field',
				field: { fieldName: 'clientName', objectTable: OBJ },
			},
			styleConfig: {
				size: 'sm',
				weight: 'medium',
				colorRule: { type: 'theme', themeColor: 'statusInfo' },
			},
			displayStyle: 'text',
		},
		lineBreak('exclusion-account-break'),
		field('exclusion-account-location', 'clientCityStateZip', muted),
	],
	label: 'Account',
	headers: [{ id: 'h-exclusion-account', text: 'Account' }],
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	sortable: true,
	sortFields: [{ direction: 'ASC', fieldName: 'clientName', objectTable: OBJ }],
	minWidth: 190,
};

/** 90-day gross sales: the size of the account. */
const salesColumn: ListBuilderColumn = {
	id: 'exclusion-sales-90',
	fields: [
		{
			id: 'exclusion-sales-90-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'clientRevenue90', objectTable: OBJ },
				defaultValue: '0',
			},
			styleConfig: {
				size: 'sm',
				weight: 'semibold',
				colorRule: { type: 'default' },
				formatOptions: { numberFormat: 'currency', decimalPlaces: 0 },
			},
			displayStyle: 'currency',
		},
	],
	label: 'Sales 90d',
	headers: [{ id: 'h-exclusion-sales-90', text: 'Sales 90d' }],
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	alignment: 'right',
	sortable: true,
	sortFields: [
		{ direction: 'DESC', fieldName: 'clientRevenue90', objectTable: OBJ },
	],
	width: 100,
};

/** Who matched: "This account", or the linked person over their role. */
const matchedColumn: ListBuilderColumn = {
	id: 'exclusion-matched',
	fields: [
		field('exclusion-matched-party', 'matchedParty', {
			...plain,
			weight: 'medium',
		}),
		lineBreak('exclusion-matched-break'),
		optionalField('exclusion-matched-role', 'matchedPartyRole', muted),
	],
	label: 'Who Matched',
	headers: [{ id: 'h-exclusion-matched', text: 'Who Matched' }],
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	sortable: false,
	width: 170,
};

/** Why: how it matched, in a few words, over the sentence (Why It's Here). */
const whyColumn: ListBuilderColumn = {
	id: 'exclusion-why',
	fields: [
		field('exclusion-matched-on', 'matchedOn', {
			...plain,
			weight: 'semibold',
		}),
		lineBreak('exclusion-why-break'),
		field('exclusion-matched-on-detail', 'matchedOnDetail', {
			...muted,
			allowTextWrap: true,
		}),
	],
	label: 'Why It’s Here',
	headers: [{ id: 'h-exclusion-why', text: 'Why It’s Here' }],
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	sortable: false,
	minWidth: 260,
};

/** Reviewed tab: the "why" in a few words only — the decision and note take the room. */
const whyShortColumn: ListBuilderColumn = {
	...whyColumn,
	id: 'exclusion-why-short',
	fields: [
		field('exclusion-matched-on-short', 'matchedOn', {
			...plain,
			weight: 'semibold',
			// "Same name at the same address" wraps rather than truncate.
			allowTextWrap: true,
		}),
	],
	label: 'Matched On',
	headers: [{ id: 'h-exclusion-why-short', text: 'Matched On' }],
	minWidth: undefined,
	width: 150,
};

/** The OIG record: name, category, address and NPI, as OIG publishes them. */
const oigRecordColumn: ListBuilderColumn = {
	id: 'exclusion-oig-record',
	fields: [
		field('exclusion-oig-name', 'oigName', { ...plain, weight: 'medium' }),
		lineBreak('exclusion-oig-break-1'),
		optionalField('exclusion-oig-category', 'oigCategory', muted),
		lineBreak('exclusion-oig-break-2'),
		field('exclusion-oig-address', 'oigAddress', { ...muted, allowTextWrap: true }),
		lineBreak('exclusion-oig-break-3'),
		{
			id: 'exclusion-oig-npi-label',
			dataConfig: { type: 'custom_text', customText: 'NPI ' },
			styleConfig: muted,
			displayStyle: 'text',
			conditionalVisibility: {
				field: 'oigNpi',
				condition: 'is_not_null',
			},
		},
		optionalField('exclusion-oig-npi', 'oigNpi', muted),
	],
	label: 'OIG Record',
	headers: [{ id: 'h-exclusion-oig-record', text: 'OIG Record' }],
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	sortable: true,
	sortFields: [{ direction: 'ASC', fieldName: 'oigName', objectTable: OBJ }],
	minWidth: 240,
};

/**
 * The exclusion: the Social Security Act authority and what it covers, the
 * date, and — where OIG lists them — when it takes effect, a reinstatement or
 * a state waiver.
 */
const exclusionColumn: ListBuilderColumn = {
	id: 'exclusion-excluded',
	fields: [
		field('exclusion-type', 'exclusionType', { ...plain, allowTextWrap: true }),
		lineBreak('exclusion-type-break'),
		{
			id: 'exclusion-date-label',
			dataConfig: { type: 'custom_text', customText: 'Excluded ' },
			styleConfig: muted,
			displayStyle: 'text',
		},
		field('exclusion-date', 'exclusionDate', muted, 'date'),
		lineBreak('exclusion-effect-break'),
		optionalField('exclusion-effect', 'exclusionEffect', {
			size: 'xs',
			colorRule: { type: 'theme', themeColor: 'statusWarning' },
		}),
		lineBreak('exclusion-reinstated-break'),
		{
			id: 'exclusion-reinstated-label',
			dataConfig: { type: 'custom_text', customText: 'Reinstated ' },
			styleConfig: muted,
			displayStyle: 'text',
			conditionalVisibility: {
				field: 'reinstatementDate',
				condition: 'is_not_null',
			},
		},
		optionalField('exclusion-reinstated', 'reinstatementDate', muted, 'date'),
		lineBreak('exclusion-waiver-break'),
		optionalField('exclusion-waiver', 'waiver', muted),
	],
	label: 'Exclusion',
	headers: [{ id: 'h-exclusion-excluded', text: 'Exclusion' }],
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	sortable: true,
	sortFields: [
		{ direction: 'DESC', fieldName: 'exclusionDate', objectTable: OBJ },
	],
	minWidth: 230,
};

/** Reviewed tab: the decision over when a follow-up comes back. */
const decisionColumn: ListBuilderColumn = {
	id: 'exclusion-decision',
	fields: [
		field('exclusion-review-outcome', 'reviewOutcome', {
			...plain,
			weight: 'semibold',
			allowTextWrap: true,
		}),
		lineBreak('exclusion-review-outcome-break'),
		optionalField('exclusion-review-follow-up', 'reviewFollowUp', {
			size: 'xs',
			colorRule: { type: 'theme', themeColor: 'statusWarning' },
		}),
	],
	label: 'Decision',
	headers: [{ id: 'h-exclusion-decision', text: 'Decision' }],
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	sortable: false,
	width: 190,
};

/**
 * Reviewed tab: the officer's note over who decided and when. The wide column
 * of this tab (`large`): it holds its minimum and takes the spare width. As a
 * plain column the table squeezed it to its "Note" header, ~50px at 1440px,
 * and a note wrapped one word per line (PD-13954).
 */
const noteColumn: ListBuilderColumn = {
	id: 'exclusion-note',
	fields: [
		field('exclusion-review-note', 'reviewNote', {
			...plain,
			allowTextWrap: true,
		}),
		lineBreak('exclusion-review-note-break'),
		field('exclusion-reviewed-by', 'reviewedBy', muted),
	],
	label: 'Note',
	headers: [{ id: 'h-exclusion-note', text: 'Note' }],
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	sortable: false,
	minWidth: 220,
	widthConfig: 'large',
};

/**
 * The row's one action: the review window, where the OIG record and our side
 * are side by side and the decision is recorded with its note. "Open" on the
 * Reviewed tab, where it shows the decision and offers Re-open.
 */
const actionColumn = (label: 'Review' | 'Open'): ListBuilderColumn => ({
	id: `exclusion-action-${label.toLowerCase()}`,
	fields: [
		{
			id: `exclusion-actions-${label.toLowerCase()}`,
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: plain,
			displayStyle: 'actions_dropdown',
			dropdownActions: [
				{
					id: `exclusion-${label.toLowerCase()}`,
					label,
					iconName: 'clipboardCheck',
					callbackName: 'reviewExclusionMatch',
					variant: label === 'Review' ? 'primary' : 'secondary',
					visibility: 'visible',
					showLabel: true,
					requiredPermission: 'licenses:update',
				},
			],
		},
	],
	label: '',
	headers: [{ id: `h-exclusion-action-${label.toLowerCase()}`, text: '' }],
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	alignment: 'right',
	sortable: false,
	width: 116,
});

const OPEN_COLUMNS: Array<ListBuilderColumn> = [
	accountColumn,
	salesColumn,
	matchedColumn,
	whyColumn,
	oigRecordColumn,
	exclusionColumn,
	actionColumn('Review'),
];

const REVIEWED_COLUMNS: Array<ListBuilderColumn> = [
	accountColumn,
	salesColumn,
	matchedColumn,
	whyShortColumn,
	oigRecordColumn,
	exclusionColumn,
	decisionColumn,
	noteColumn,
	actionColumn('Open'),
];

const strongList: ListBuilderList = {
	id: FEDERAL_EXCLUSION_LIST_IDS.strong,
	cacheTTL: 300000,
	name: 'Strong Matches',
	type: 'default',
	active: true,
	sortOrder: 1,
	filterRules: {
		exclusionView: {
			defaultValue: {
				id: 'strong',
				label: 'Same NPI, or same name at the same address',
			},
			locked: true,
			hidden: true,
		},
	},
	columns: OPEN_COLUMNS,
	defaultSortId: 'sales-90-desc',
};

const nameOnlyList: ListBuilderList = {
	id: FEDERAL_EXCLUSION_LIST_IDS.nameOnly,
	cacheTTL: 300000,
	name: 'Check Your Records',
	type: 'default',
	active: true,
	sortOrder: 2,
	filterRules: {
		exclusionView: {
			defaultValue: { id: 'name_only', label: 'Same name in the same state' },
			locked: true,
			hidden: true,
		},
	},
	columns: OPEN_COLUMNS,
	defaultSortId: 'sales-90-desc',
};

const reviewedList: ListBuilderList = {
	id: FEDERAL_EXCLUSION_LIST_IDS.reviewed,
	cacheTTL: 300000,
	name: 'Reviewed',
	type: 'default',
	active: true,
	sortOrder: 3,
	filterRules: {
		exclusionView: {
			defaultValue: { id: 'reviewed', label: 'Reviewed' },
			locked: true,
			hidden: true,
		},
	},
	columns: REVIEWED_COLUMNS,
	// Follow-ups coming due first, then the largest accounts. Curated here: a
	// follow-up date means nothing on the open tabs.
	sortOptions: [
		{
			id: 'follow-up-soonest',
			name: 'Follow-Up Date (Soonest First)',
			description: 'Follow-ups coming due first, then the other decisions',
			fields: [
				{ direction: 'ASC', fieldName: 'followUpDate', objectTable: OBJ },
				{ direction: 'DESC', fieldName: 'clientRevenue90', objectTable: OBJ },
			],
		},
		{
			id: 'sales-90-desc',
			name: 'Sales 90 Days (High to Low)',
			description: 'Largest accounts first',
			fields: [
				{ direction: 'DESC', fieldName: 'clientRevenue90', objectTable: OBJ },
				{ direction: 'ASC', fieldName: 'clientName', objectTable: OBJ },
			],
		},
	],
	defaultSortId: 'follow-up-soonest',
};

export const FEDERAL_EXCLUSION_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [strongList, nameOnlyList, reviewedList],
	paging: { defaultPageSize: 25, pageSizeOptions: [10, 25, 50, 100] },
	showResetButton: true,
	filters: {
		activeFilters: [
			'exclusionKeywords',
			'exclusionView',
			'exclusionMatchedOn',
			'exclusionSubject',
		],
		defaultFilters: {},
	},
	tableSorts: [
		{
			id: 'sales-90-desc',
			name: 'Sales 90 Days (High to Low)',
			description: 'Largest accounts first',
			fields: [
				{ direction: 'DESC', fieldName: 'clientRevenue90', objectTable: OBJ },
				{ direction: 'ASC', fieldName: 'clientName', objectTable: OBJ },
			],
		},
		{
			id: 'exclusion-date-desc',
			name: 'Exclusion Date (Newest First)',
			description: 'The most recent OIG exclusions first',
			fields: [
				{ direction: 'DESC', fieldName: 'exclusionDate', objectTable: OBJ },
			],
		},
	],
	objects: [
		{
			baseUrl: FEDERAL_EXCLUSION_PATH,
			objectLabel: 'Federal exclusion match',
			objectTable: OBJ,
			selectedFields: [
				{ fieldName: 'clientName', fieldLabel: 'Account' },
				{ fieldName: 'clientCityStateZip', fieldLabel: 'City/State/Zip' },
				{ fieldName: 'clientRevenue90', fieldLabel: 'Sales 90d' },
				{ fieldName: 'matchedParty', fieldLabel: 'Who Matched' },
				{ fieldName: 'matchedPartyRole', fieldLabel: 'Role' },
				{ fieldName: 'matchedOn', fieldLabel: 'Matched On' },
				{ fieldName: 'matchedOnDetail', fieldLabel: 'Why It’s Here' },
				{ fieldName: 'oigName', fieldLabel: 'OIG Name' },
				{ fieldName: 'oigCategory', fieldLabel: 'OIG Category' },
				{ fieldName: 'oigAddress', fieldLabel: 'OIG Address' },
				{ fieldName: 'oigNpi', fieldLabel: 'OIG NPI' },
				{ fieldName: 'exclusionType', fieldLabel: 'Exclusion Type' },
				{ fieldName: 'exclusionDate', fieldLabel: 'Exclusion Date' },
				{ fieldName: 'reinstatementDate', fieldLabel: 'Reinstated' },
				{ fieldName: 'reviewOutcome', fieldLabel: 'Decision' },
				{ fieldName: 'reviewNote', fieldLabel: 'Note' },
			],
		},
		// The Account column links to /clients/:id.
		{
			baseUrl: '/clients/:id',
			objectLabel: 'Client',
			objectTable: 'client',
			selectedFields: [],
		},
	],
};
